import { api } from "./_client";
import { pollAiJob } from "./ai";

export async function createEstimate(payload, onStage, onJobId) {
    const { data: job } = await api.post(`/estimate`, payload);
    onJobId && onJobId(job.id);
    return pollAiJob(job.id, { onStage });
}

// iter-98 — Revise an existing estimate with user answers to its
// open questions. Saves the AI's revised output as a brand-new
// estimate so the original is preserved. ``answers`` is an array of
// ``{question, answer}`` objects.

export async function reviseEstimate(estimateId, answers, onStage, onJobId) {
    const { data: job } = await api.post(
        `/estimates/${estimateId}/revise`,
        { answers },
    );
    onJobId && onJobId(job.id);
    return pollAiJob(job.id, { onStage });
}

// --- Takeoff (AI-powered blueprint measurement) ---

export async function listEstimates(projectId) {
    const params = projectId ? { project_id: projectId } : {};
    const { data } = await api.get(`/estimates`, { params });
    return data;
}

export async function listAllEstimates() {
    const { data } = await api.get(`/estimates`);
    return data;
}

export async function updateEstimate(id, payload) {
    const { data } = await api.patch(`/estimates/${id}`, payload);
    return data;
}

// iter-136 — variant that includes a concurrent-save guard. `payload`
// is the update fields; `expectedSaves` is the `saves` counter the
// frontend loaded. Backend returns 409 if another teammate saved in
// the meantime — the axios error carries `response.data.detail` with
// `{code, message, current_saves, last_saved_by_name, last_saved_at}`
// so the caller can show a targeted toast and refresh.
export async function updateEstimateGuarded(id, payload, expectedSaves) {
    const body = { ...payload };
    if (typeof expectedSaves === "number") body.expected_saves = expectedSaves;
    const { data } = await api.patch(`/estimates/${id}`, body);
    return data;
}

export async function lockEstimate(id) {
    const { data } = await api.post(`/estimates/${id}/lock`);
    return data;
}

export async function unlockEstimate(id) {
    const { data } = await api.post(`/estimates/${id}/unlock`);
    return data;
}

export async function deleteEstimate(id) {
    const { data } = await api.delete(`/estimates/${id}`);
    return data;
}

// iter-110: retro-fill description column on an existing estimate
// (cheap single LLM call, no full re-price). Used by the "Fill
// descriptions" button on ReportsPage / EstimateResultDialog.
export async function fillEstimateDescriptions(id) {
    const { data } = await api.post(`/estimates/${id}/fill-descriptions`);
    return data;
}

// iter-216 — Audit duplicates on a saved estimate. Collapses semantic
// duplicates produced by the batched merge on massive tenders (e.g.
// mobilization emitted 20× under different wording across chunks).
// Returns { removed, before, after, estimate }.
export async function auditEstimateDuplicates(id) {
    const { data } = await api.post(`/estimates/${id}/audit-duplicates`);
    return data;
}

// Free second-pass review from the local Ollama model (Settings -> AI Use).
// GET returns { enabled, model, review } ; POST re-runs it on the current lines.
export async function getLocalReview(id) {
    const { data } = await api.get(`/estimates/${id}/local-review`);
    return data;
}
export async function rerunLocalReview(id) {
    const { data } = await api.post(`/estimates/${id}/local-review`);
    return data;
}


// --- Template CSV import / export ---

export async function fetchQuotePdfBlob(estimateId, quoteData) {
    const { data } = await api.post(
        `/estimates/${estimateId}/quote-pdf`,
        quoteData,
        { responseType: "blob" },
    );
    return data; // Blob (application/pdf)
}

// iter-102 — email scanner / auto-quote per-account config
